export const INDUSTRY_IDS = {
  BEAUTY: 'beauty',
  HEALTH_FITNESS: 'health_fitness',
  CONSULTING: 'consulting',
  ENTERTAINMENT_LIVE_PRODUCTION: 'entertainment_live_production',
  LANDSCAPING: 'landscaping',
  CARPENTRY: 'carpentry',
  PAINTING_DECORATING: 'painting_decorating',
  ELECTRICAL: 'electrical'
};

export const DEFAULT_INDUSTRY_ID = INDUSTRY_IDS.BEAUTY;

const withDefaultIcon = (items) => items.map((item) => ({ icon: '', ...item }));

const COMMON_INCOME = withDefaultIcon([
  { id: 'gift_card', name: 'Gift Card / Voucher' },
  { id: 'training', name: 'Training Services' },
  { id: 'gov_funding', name: 'Gov Funding' },
  { id: 'loan', name: 'Loan' }
]);

const COMMON_EXPENSES = withDefaultIcon([
  { id: 'general_expenses', name: 'General Expenses' },
  { id: 'utilities', name: 'Utilities' },
  { id: 'internet_telephone', name: 'Internet/Telephone' },
  { id: 'rent', name: 'Rent/Lease' },
  { id: 'staffCosts', name: 'Staff Costs' },
  { id: 'employer_prsi', name: 'Employer PRSI' },
  { id: 'employee_holiday_pay', name: 'Employee Holiday Pay' },
  { id: 'owner_pension', name: 'Owner Pension' },
  { id: 'naersa_employer_contributions', name: 'NAERSA Employer Contributions' },
  { id: 'naersa_employee_contributions', name: 'NAERSA Employee Contributions' },
  { id: 'insurance', name: 'Insurance' },
  { id: 'marketing', name: 'Marketing' },
  { id: 'travel_expenses', name: 'Travel' },
  { id: 'meals', name: 'Meals' },
  { id: 'uniform_costs', name: 'Uniform / Workwear' },
  { id: 'equipment', name: 'Equipment' },
  { id: 'vehicle', name: 'Vehicle Purchase' },
  { id: 'vehicle_fuel', name: 'Vehicle Fuel' },
  { id: 'vehicle_repairs_licensing', name: 'Vehicle Repairs & Licensing' },
  { id: 'accounting', name: 'Accounting' },
  { id: 'legal', name: 'Legal Services' },
  { id: 'interest', name: 'Interest' },
  { id: 'bank_charges', name: 'Bank Charges' },
  { id: 'loan_repayment', name: 'Loan Repayment' },
  { id: 'training', name: 'Courses & Learning' },
  { id: 'repairs', name: 'Repairs' },
  { id: 'cleaning', name: 'Cleaning' },
  { id: 'maintenance', name: 'Equipment Maintenance' },
  { id: 'software', name: 'Software/Apps' },
  { id: 'office', name: 'Office Supplies' },
  { id: 'misc', name: 'Miscellaneous' }
]);

const CONSTRUCTION_RCT_INCOME = withDefaultIcon([
  { id: 'rct_contract_income', name: 'RCT Contract Income' },
  { id: 'rct_tax_income', name: 'RCT Tax Income' }
]);

const CONSTRUCTION_RCT_EXPENSES = withDefaultIcon([
  { id: 'subcontractor_rct', name: 'Subcontractor (RCT)' },
  { id: 'outsourced_labour_non_rct', name: 'Outsourced Labour (Non RCT)' },
  { id: 'rct_tax', name: 'RCT Tax' }
]);

const merge = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (!item?.id) return;
    map.set(item.id, { icon: '', ...(map.get(item.id) || {}), ...item });
  });
  return Array.from(map.values());
};

const beautyIncome = withDefaultIcon([
  { id: 'facial', name: 'Facial Treatments' },
  { id: 'injectables', name: 'Injectables' },
  { id: 'body_treatments', name: 'Body Treatments' },
  { id: 'massage', name: 'Massage Services' },
  { id: 'hair', name: 'Hair Services' },
  { id: 'nails', name: 'Nail Services' },
  { id: 'brows', name: 'Brow Services' },
  { id: 'lashes', name: 'Lash Services' },
  { id: 'makeup', name: 'Makeup Services' },
  { id: 'retail', name: 'Retail Products' },
  { id: 'packages', name: 'Beauty Packages' },
  { id: 'consulting', name: 'Beauty Consulting' }
]);

const beautyExpenses = withDefaultIcon([
  { id: 'supplies', name: 'Supplies/Materials' },
  { id: 'retail_stock', name: 'Retail Products' },
  { id: 'salon_tools', name: 'Salon Tools' },
  { id: 'furniture', name: 'Furniture' },
  { id: 'electronics', name: 'Electronics' },
  { id: 'medical_expenses', name: 'Medical Expenses' }
]);

export const INDUSTRY_PROFILES = {
  [INDUSTRY_IDS.BEAUTY]: {
    id: INDUSTRY_IDS.BEAUTY,
    label: 'Beauty & Wellness',
    shortLabel: 'Beauty',
    description: 'Salons, spas, aestheticians, makeup artists, hair, nails, brows, lashes.',
    categories: {
      income: merge(beautyIncome, COMMON_INCOME),
      expenses: merge(beautyExpenses, COMMON_EXPENSES)
    },
    features: {
      supportsProjects: false,
      supportsRct: false
    },
    pricingTerminology: {
      itemSingular: 'service',
      itemPlural: 'services',
      itemNameLabel: 'Service Name',
      itemListHeading: 'Available Services',
      manageItemsLabel: 'Manage Services',
      addItemLabel: 'Add New Service',
      editItemLabel: 'Edit Service',
      publishLabel: 'Publish as Service',
      directCostLabel: 'Cost of Products',
      directCostHelper: 'Products used in this service',
      directCostEmpty: 'No saved cost items for this category yet. Add products in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Service Labour Source',
      timeRequiredLabel: 'Time Required (minutes)'
    },
    servicePresets: {}
  },
  [INDUSTRY_IDS.HEALTH_FITNESS]: {
    id: INDUSTRY_IDS.HEALTH_FITNESS,
    label: 'Health & Fitness',
    shortLabel: 'Fitness',
    description: 'Personal trainers, small gyms, pilates/yoga studios, sports camps.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'personal_training', name: 'Personal Training' },
        { id: 'group_classes', name: 'Group Classes' },
        { id: 'memberships', name: 'Memberships' },
        { id: 'pilates_yoga', name: 'Pilates / Yoga' },
        { id: 'sports_camps', name: 'Sports Camps' },
        { id: 'nutrition_coaching', name: 'Nutrition Coaching' },
        { id: 'fitness_retail', name: 'Retail Products' }
      ]), COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'fitness_equipment', name: 'Fitness Equipment' },
        { id: 'studio_supplies', name: 'Studio Supplies' },
        { id: 'cleaning_hygiene', name: 'Cleaning & Hygiene' },
        { id: 'sports_kit', name: 'Sports Kit / Apparel' },
        { id: 'music_licensing', name: 'Music Licensing' },
        { id: 'booking_software', name: 'Booking Software' }
      ]), COMMON_EXPENSES)
    },
    features: {
      supportsProjects: false,
      supportsRct: false
    },
    pricingTerminology: {
      itemSingular: 'session',
      itemPlural: 'sessions',
      itemNameLabel: 'Session / Class Name',
      itemListHeading: 'Available Sessions',
      manageItemsLabel: 'Manage Sessions',
      addItemLabel: 'Add New Session',
      editItemLabel: 'Edit Session',
      publishLabel: 'Publish as Session',
      directCostLabel: 'Session Materials / Equipment Cost',
      directCostHelper: 'Consumables, equipment use, or kit used for this session',
      directCostEmpty: 'No saved cost items for this category yet. Add equipment or consumables in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Coach / Instructor Labour Source',
      timeRequiredLabel: 'Session Length (minutes)'
    },
    servicePresets: {
      personal_training: [
        { name: '1:1 Personal Training', duration: 60, cost: 0, price: 0 },
        { name: 'Small Group PT', duration: 60, cost: 0, price: 0 },
        { name: 'Fitness Assessment', duration: 45, cost: 0, price: 0 }
      ],
      group_classes: [
        { name: 'Group Fitness Class', duration: 45, cost: 0, price: 0 },
        { name: 'HIIT Class', duration: 45, cost: 0, price: 0 },
        { name: 'Bootcamp Session', duration: 60, cost: 0, price: 0 }
      ],
      pilates_yoga: [
        { name: 'Pilates Class', duration: 50, cost: 0, price: 0 },
        { name: 'Yoga Class', duration: 60, cost: 0, price: 0 },
        { name: 'Private Pilates Session', duration: 60, cost: 0, price: 0 }
      ],
      sports_camps: [
        { name: 'Half-Day Sports Camp', duration: 180, cost: 0, price: 0 },
        { name: 'Full-Day Sports Camp', duration: 360, cost: 0, price: 0 }
      ]
    }
  },
  [INDUSTRY_IDS.CONSULTING]: {
    id: INDUSTRY_IDS.CONSULTING,
    label: 'Consulting',
    shortLabel: 'Consulting',
    description: 'Self-employed business consultants, doctors, management consultants, project managers.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'consulting_fees', name: 'Consulting Fees' },
        { id: 'management_consulting', name: 'Management Consulting' },
        { id: 'business_advisory', name: 'Business Advisory' },
        { id: 'project_management', name: 'Project Management' },
        { id: 'medical_consulting', name: 'Medical Consulting' },
        { id: 'retainer_income', name: 'Retainer Income' },
        { id: 'workshops_training', name: 'Workshops & Training' }
      ]), COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'professional_memberships', name: 'Professional Memberships' },
        { id: 'professional_indemnity', name: 'Professional Indemnity' },
        { id: 'research_reports', name: 'Research & Reports' },
        { id: 'client_meetings', name: 'Client Meetings' },
        { id: 'contractor_support', name: 'Contractor Support' },
        { id: 'medical_equipment', name: 'Medical Equipment' }
      ]), COMMON_EXPENSES)
    },
    features: {
      supportsProjects: true,
      supportsRct: false
    },
    pricingTerminology: {
      itemSingular: 'engagement',
      itemPlural: 'engagements',
      itemNameLabel: 'Engagement Name',
      itemListHeading: 'Available Engagements',
      manageItemsLabel: 'Manage Engagements',
      addItemLabel: 'Add New Engagement',
      editItemLabel: 'Edit Engagement',
      publishLabel: 'Publish as Engagement',
      directCostLabel: 'Engagement Direct Costs',
      directCostHelper: 'Research, subcontractor support, travel, or materials needed for this engagement',
      directCostEmpty: 'No saved direct costs for this category yet. Add engagement costs in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Consultant Labour Source',
      timeRequiredLabel: 'Engagement Time (minutes)'
    },
    servicePresets: {
      consulting_fees: [
        { name: 'Hourly Consulting Session', duration: 60, cost: 0, price: 0 },
        { name: 'Half-Day Consulting Engagement', duration: 240, cost: 0, price: 0 },
        { name: 'Full-Day Consulting Engagement', duration: 480, cost: 0, price: 0 }
      ],
      project_management: [
        { name: 'Project Management Day', duration: 480, cost: 0, price: 0 },
        { name: 'Project Review Meeting', duration: 90, cost: 0, price: 0 }
      ],
      workshops_training: [
        { name: 'Client Workshop', duration: 180, cost: 0, price: 0 },
        { name: 'Training Session', duration: 120, cost: 0, price: 0 }
      ]
    }
  },
  [INDUSTRY_IDS.ENTERTAINMENT_LIVE_PRODUCTION]: {
    id: INDUSTRY_IDS.ENTERTAINMENT_LIVE_PRODUCTION,
    label: 'Entertainment & Live Production',
    shortLabel: 'Live Production',
    description: 'Roadies, live sound techs, film crews, lighting designers, event production teams.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'live_production', name: 'Live Production' },
        { id: 'sound_engineering', name: 'Sound Engineering' },
        { id: 'lighting_design', name: 'Lighting Design' },
        { id: 'film_crew', name: 'Film Crew' },
        { id: 'stage_crew', name: 'Stage Crew / Roadie Work' },
        { id: 'equipment_hire_income', name: 'Equipment Hire Income' },
        { id: 'production_management', name: 'Production Management' }
      ]), COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'crew_labour', name: 'Crew Labour' },
        { id: 'audio_equipment', name: 'Audio Equipment' },
        { id: 'lighting_equipment', name: 'Lighting Equipment' },
        { id: 'camera_equipment', name: 'Camera Equipment' },
        { id: 'production_consumables', name: 'Production Consumables' },
        { id: 'venue_site_costs', name: 'Venue / Site Costs' },
        { id: 'permits_licensing', name: 'Permits & Licensing' },
        { id: 'freight_transport', name: 'Freight & Transport' }
      ]), COMMON_EXPENSES)
    },
    features: {
      supportsProjects: true,
      supportsRct: false
    },
    pricingTerminology: {
      itemSingular: 'gig',
      itemPlural: 'gigs',
      itemNameLabel: 'Gig / Production Name',
      itemListHeading: 'Available Gigs',
      manageItemsLabel: 'Manage Gigs',
      addItemLabel: 'Add New Gig',
      editItemLabel: 'Edit Gig',
      publishLabel: 'Publish as Gig',
      directCostLabel: 'Production Direct Costs',
      directCostHelper: 'Crew, equipment hire, consumables, transport, or site costs for this production',
      directCostEmpty: 'No saved production costs for this category yet. Add crew or equipment costs in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Production Labour Source',
      timeRequiredLabel: 'Production Time (minutes)'
    },
    servicePresets: {
      live_production: [
        { name: 'Live Production Day', duration: 600, cost: 0, price: 0 },
        { name: 'Event Load-In / Load-Out', duration: 240, cost: 0, price: 0 }
      ],
      sound_engineering: [
        { name: 'Live Sound Engineer', duration: 480, cost: 0, price: 0 },
        { name: 'Sound Check / Setup', duration: 120, cost: 0, price: 0 }
      ],
      lighting_design: [
        { name: 'Lighting Design Day', duration: 480, cost: 0, price: 0 },
        { name: 'Lighting Operator Shift', duration: 360, cost: 0, price: 0 }
      ],
      film_crew: [
        { name: 'Film Crew Day Rate', duration: 600, cost: 0, price: 0 },
        { name: 'Half-Day Film Crew Booking', duration: 300, cost: 0, price: 0 }
      ]
    }
  },
  [INDUSTRY_IDS.LANDSCAPING]: {
    id: INDUSTRY_IDS.LANDSCAPING,
    label: 'Landscaping',
    shortLabel: 'Landscaping',
    description: 'Garden maintenance, landscaping projects, planting, lawns, outdoor work.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'garden_maintenance', name: 'Garden Maintenance' },
        { id: 'landscaping_projects', name: 'Landscaping Projects' },
        { id: 'lawn_care', name: 'Lawn Care' },
        { id: 'planting_design', name: 'Planting & Design' },
        { id: 'hedge_tree_work', name: 'Hedge / Tree Work' },
        { id: 'maintenance_contracts', name: 'Maintenance Contracts' }
      ]), CONSTRUCTION_RCT_INCOME, COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'plants_soil_materials', name: 'Plants, Soil & Materials' },
        { id: 'landscaping_tools', name: 'Landscaping Tools' },
        { id: 'machinery_hire', name: 'Machinery Hire' },
        { id: 'green_waste', name: 'Green Waste Disposal' },
        { id: 'protective_workwear', name: 'Protective Workwear' }
      ]), CONSTRUCTION_RCT_EXPENSES, COMMON_EXPENSES)
    },
    features: {
      supportsProjects: true,
      supportsRct: true
    },
    pricingTerminology: {
      itemSingular: 'job',
      itemPlural: 'jobs',
      itemNameLabel: 'Job Name',
      itemListHeading: 'Available Jobs',
      manageItemsLabel: 'Manage Jobs',
      addItemLabel: 'Add New Job',
      editItemLabel: 'Edit Job',
      publishLabel: 'Publish as Job',
      directCostLabel: 'Materials / Plants Cost',
      directCostHelper: 'Plants, soil, seed, timber, aggregate, or other job materials',
      directCostEmpty: 'No saved materials for this category yet. Add materials in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Job Labour Source',
      timeRequiredLabel: 'Job Time (minutes)'
    },
    servicePresets: {
      garden_maintenance: [
        { name: 'Garden Maintenance Visit', duration: 120, cost: 0, price: 0 },
        { name: 'Hedge Trimming', duration: 180, cost: 0, price: 0 },
        { name: 'Lawn Cutting', duration: 60, cost: 0, price: 0 }
      ],
      landscaping_projects: [
        { name: 'Small Landscaping Project', duration: 480, cost: 0, price: 0 },
        { name: 'Patio / Paving Job', duration: 480, cost: 0, price: 0 }
      ]
    }
  },
  [INDUSTRY_IDS.CARPENTRY]: {
    id: INDUSTRY_IDS.CARPENTRY,
    label: 'Carpentry',
    shortLabel: 'Carpentry',
    description: 'Carpenters, joiners, cabinetry, fit-outs, repairs and installations.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'carpentry_labour', name: 'Carpentry Labour' },
        { id: 'joinery', name: 'Joinery' },
        { id: 'cabinetry', name: 'Cabinetry' },
        { id: 'fit_outs', name: 'Fit-Outs' },
        { id: 'repairs_installations', name: 'Repairs & Installations' },
        { id: 'maintenance_contracts', name: 'Maintenance Contracts' }
      ]), CONSTRUCTION_RCT_INCOME, COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'timber_sheet_materials', name: 'Timber & Sheet Materials' },
        { id: 'fixings_hardware', name: 'Fixings & Hardware' },
        { id: 'carpentry_tools', name: 'Carpentry Tools' },
        { id: 'tool_hire', name: 'Tool Hire' },
        { id: 'workwear_ppe', name: 'Workwear / PPE' }
      ]), CONSTRUCTION_RCT_EXPENSES, COMMON_EXPENSES)
    },
    features: {
      supportsProjects: true,
      supportsRct: true
    },
    pricingTerminology: {
      itemSingular: 'job',
      itemPlural: 'jobs',
      itemNameLabel: 'Job Name',
      itemListHeading: 'Available Jobs',
      manageItemsLabel: 'Manage Jobs',
      addItemLabel: 'Add New Job',
      editItemLabel: 'Edit Job',
      publishLabel: 'Publish as Job',
      directCostLabel: 'Materials Cost',
      directCostHelper: 'Timber, sheet materials, fixings, hardware, and consumables',
      directCostEmpty: 'No saved materials for this category yet. Add materials in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Job Labour Source',
      timeRequiredLabel: 'Job Time (minutes)'
    },
    servicePresets: {
      carpentry_labour: [
        { name: 'Half-Day Carpentry Job', duration: 240, cost: 0, price: 0 },
        { name: 'Full-Day Carpentry Job', duration: 480, cost: 0, price: 0 }
      ],
      cabinetry: [
        { name: 'Cabinet Installation', duration: 360, cost: 0, price: 0 },
        { name: 'Custom Cabinet Build', duration: 480, cost: 0, price: 0 }
      ]
    }
  },
  [INDUSTRY_IDS.PAINTING_DECORATING]: {
    id: INDUSTRY_IDS.PAINTING_DECORATING,
    label: 'Painting & Decorating',
    shortLabel: 'Painting',
    description: 'Interior/exterior painting, decorating, wallpapering, prep and repairs.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'interior_painting', name: 'Interior Painting' },
        { id: 'exterior_painting', name: 'Exterior Painting' },
        { id: 'decorating', name: 'Decorating' },
        { id: 'wallpapering', name: 'Wallpapering' },
        { id: 'prep_repairs', name: 'Prep & Repairs' },
        { id: 'maintenance_contracts', name: 'Maintenance Contracts' }
      ]), CONSTRUCTION_RCT_INCOME, COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'paint_materials', name: 'Paint & Materials' },
        { id: 'decorating_tools', name: 'Decorating Tools' },
        { id: 'dust_sheets_protection', name: 'Dust Sheets & Protection' },
        { id: 'access_equipment_hire', name: 'Access Equipment Hire' },
        { id: 'workwear_ppe', name: 'Workwear / PPE' }
      ]), CONSTRUCTION_RCT_EXPENSES, COMMON_EXPENSES)
    },
    features: {
      supportsProjects: true,
      supportsRct: true
    },
    pricingTerminology: {
      itemSingular: 'job',
      itemPlural: 'jobs',
      itemNameLabel: 'Job Name',
      itemListHeading: 'Available Jobs',
      manageItemsLabel: 'Manage Jobs',
      addItemLabel: 'Add New Job',
      editItemLabel: 'Edit Job',
      publishLabel: 'Publish as Job',
      directCostLabel: 'Paint / Materials Cost',
      directCostHelper: 'Paint, filler, caulk, tape, sheets, and decorating consumables',
      directCostEmpty: 'No saved materials for this category yet. Add paint or decorating materials in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Job Labour Source',
      timeRequiredLabel: 'Job Time (minutes)'
    },
    servicePresets: {
      interior_painting: [
        { name: 'Single Room Painting', duration: 480, cost: 0, price: 0 },
        { name: 'Feature Wall', duration: 180, cost: 0, price: 0 }
      ],
      wallpapering: [
        { name: 'Wallpaper Feature Wall', duration: 240, cost: 0, price: 0 }
      ]
    }
  },
  [INDUSTRY_IDS.ELECTRICAL]: {
    id: INDUSTRY_IDS.ELECTRICAL,
    label: 'Electrical',
    shortLabel: 'Electrical',
    description: 'Electricians, call-outs, installations, testing, repairs and maintenance.',
    categories: {
      income: merge(withDefaultIcon([
        { id: 'electrical_call_outs', name: 'Call-Outs' },
        { id: 'installations', name: 'Installations' },
        { id: 'testing_certification', name: 'Testing & Certification' },
        { id: 'repairs_fault_finding', name: 'Repairs & Fault Finding' },
        { id: 'maintenance_contracts', name: 'Maintenance Contracts' },
        { id: 'materials_recharged', name: 'Materials Recharged' }
      ]), CONSTRUCTION_RCT_INCOME, COMMON_INCOME),
      expenses: merge(withDefaultIcon([
        { id: 'electrical_materials', name: 'Electrical Materials' },
        { id: 'electrical_tools', name: 'Electrical Tools' },
        { id: 'testing_equipment', name: 'Testing Equipment' },
        { id: 'certification_fees', name: 'Certification Fees' },
        { id: 'workwear_ppe', name: 'Workwear / PPE' }
      ]), CONSTRUCTION_RCT_EXPENSES, COMMON_EXPENSES)
    },
    features: {
      supportsProjects: true,
      supportsRct: true
    },
    pricingTerminology: {
      itemSingular: 'job',
      itemPlural: 'jobs',
      itemNameLabel: 'Job Name',
      itemListHeading: 'Available Jobs',
      manageItemsLabel: 'Manage Jobs',
      addItemLabel: 'Add New Job',
      editItemLabel: 'Edit Job',
      publishLabel: 'Publish as Job',
      directCostLabel: 'Materials Cost',
      directCostHelper: 'Cable, fittings, switches, sockets, consumer units, and job materials',
      directCostEmpty: 'No saved materials for this category yet. Add electrical materials in the Cost Calculator to reuse them here.',
      labourSourceLabel: 'Job Labour Source',
      timeRequiredLabel: 'Job Time (minutes)'
    },
    servicePresets: {
      electrical_call_outs: [
        { name: 'Standard Call-Out', duration: 60, cost: 0, price: 0 },
        { name: 'Emergency Call-Out', duration: 60, cost: 0, price: 0 }
      ],
      testing_certification: [
        { name: 'Electrical Inspection', duration: 120, cost: 0, price: 0 },
        { name: 'Certification Visit', duration: 90, cost: 0, price: 0 }
      ],
      installations: [
        { name: 'Socket / Switch Installation', duration: 90, cost: 0, price: 0 },
        { name: 'Consumer Unit Installation', duration: 360, cost: 0, price: 0 }
      ]
    }
  }
};

export const INDUSTRY_OPTIONS = Object.values(INDUSTRY_PROFILES).map((profile) => ({
  id: profile.id,
  label: profile.label,
  description: profile.description
}));

export const normalizeIndustryId = (value) => (
  INDUSTRY_PROFILES[value] ? value : DEFAULT_INDUSTRY_ID
);

export const getIndustryProfile = (value) => (
  INDUSTRY_PROFILES[normalizeIndustryId(value)]
);
